import { Link } from "react-router";

import { ArrowRight } from "@/components/ui/Button";

/**
 * Three ways in.
 *
 * This replaced a six-row matrix that named every kind of visitor and gave each
 * one a sentence. It was accurate and it was a decision tree: a funder had to
 * read past five other rows to find the one addressed to them, and the homepage
 * carried six competing calls to action before the programmes had been shown.
 *
 * Three cards, each collecting several audiences that want the same next step.
 * The detail those six rows carried has not been deleted; it lives on the pages
 * these link to, which is where somebody who has chosen a route will read it.
 */
export type HomeAction = {
  title: string;
  who: string;
  body: string;
  href: string;
  cta: string;
};

export const defaultActions: HomeAction[] = [
  {
    title: "Support our work",
    who: "Foundations, INGOs, embassies, development agencies, companies and CSR teams.",
    body: "Legal identity, the delivery record, dated outcomes and the documents due diligence needs.",
    href: "/get-involved/support",
    cta: "Explore support options",
  },
  {
    title: "Volunteer or join Charity Trek",
    who: "Volunteers, students, travellers and service groups.",
    body: "Real roles on live programmes, honest costs, and Charity Trek run with Mountain Routes.",
    href: "/get-involved/volunteer",
    cta: "See how volunteering works",
  },
  {
    title: "Request local collaboration",
    who: "Schools, municipalities, wards, health posts, CBOs and community groups.",
    body: "How to request a session, a health camp or an infrastructure project in your community.",
    href: "/contact",
    cta: "Propose local collaboration",
  },
];

export function ActionCards({ actions = defaultActions }: { actions?: HomeAction[] }) {
  return (
    <ul className="grid gap-6 md:grid-cols-3">
      {actions.map((action) => (
        <li key={action.title}>
          <Link
            to={action.href}
            className="group relative flex h-full flex-col rounded-[20px] border border-line bg-white p-7 shadow-[0_1px_2px_rgba(12,29,51,0.04),0_12px_28px_-22px_rgba(12,29,51,0.22)] transition-[transform,box-shadow,border-color] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-1 hover:border-line-strong hover:shadow-[0_1px_2px_rgba(12,29,51,0.05),0_28px_50px_-28px_rgba(12,29,51,0.34)] md:p-8"
          >
            <h3 className="font-serif" style={{ fontSize: "var(--step-h4)" }}>
              {action.title}
            </h3>
            <p className="mt-2.5 text-[0.875rem] text-muted">{action.who}</p>
            <p className="mt-5 text-[0.9375rem] text-ink">{action.body}</p>

            <span className="mt-auto inline-flex items-center gap-2 pt-7 text-[0.9375rem] font-semibold text-navy">
              {action.cta}
              <ArrowRight className="transition-transform duration-400 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-1.5" />
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
